/**
 * The `sol-ask` WIRE CONTRACT, as the client sees it. M9 / S09-04.
 *
 * **A MIRROR, AND MIRRORS DRIFT — SO SAY SO.** The authority is
 * `supabase/functions/_shared/sol-ask.ts` and `_shared/sol-reasoning.ts`. They
 * cannot be imported here: `supabase/functions/` is a Deno tree outside this
 * project's `tsconfig` roots, and pulling it in would drag a Deno runtime
 * surface into the browser bundle. `capture-planning.ts` restates the database
 * enums for the same reason and records the same caveat.
 *
 * **THE MIRROR IS NARROW ON PURPOSE.** Only what a screen has to render is
 * restated. Nothing here describes evidence, transcripts, prompts, budgets or
 * providers, because none of that crosses the wire and the client must not
 * start believing it does.
 *
 * **NOTHING HERE IS AUTHORITATIVE.** Counts, scope, broadening, source identity
 * and answer state are decided on the server. These types describe the shape
 * they arrive in; they never re-derive the values.
 */

import type { CaptureStatus } from '../database.types';

// ===========================================================================
// Identity
// ===========================================================================

/** A source identity: the two fields every future Sol domain can supply. */
export interface SolSourceRef {
  readonly domain: 'capture';
  /** The internal UUID. **Never shown to Brandon, never spoken.** */
  readonly id: string;
}

/**
 * Project identity.
 *
 * **`name` IS GENUINELY NULLABLE**, and the id is never a fallback for it —
 * see `SAFE_PROJECT_FALLBACK` in `../../features/sol/sol-wording.ts`.
 */
export interface SolProjectRef {
  readonly id: string;
  readonly name: string | null;
}

// ===========================================================================
// Scope
// ===========================================================================

export type CaptureScope =
  { readonly kind: 'project'; readonly projectId: string } | { readonly kind: 'global' };

/** Deterministic, server-owned facts about the search that was executed. */
export interface SolScopeFacts {
  readonly requestedScope: CaptureScope;
  readonly scopesSearched: readonly CaptureScope[];
  readonly evidenceScope: CaptureScope | null;
  readonly broadenedFrom: CaptureScope | null;
  readonly hardScope: boolean;
  readonly projectsRepresented: readonly string[];
}

// ===========================================================================
// Planner outcomes
// ===========================================================================

export type ClarificationReason =
  | 'ambiguous_project_name'
  | 'unresolved_project_name'
  | 'near_project_name'
  | 'project_context_required'
  | 'scope_undetermined'
  | 'contradictory_scope'
  | 'ambiguous_physical_identifier'
  | 'ambiguous_status_language';

export type UnsupportedReason =
  | 'negation'
  | 'unsupported_relative_date'
  | 'superlative_or_comparison'
  | 'conversational_reference'
  | 'multi_project_comparison';

// ===========================================================================
// Grounded answers
// ===========================================================================

export type SolGrounding = 'supported' | 'inference' | 'conflicting' | 'general';
export type SolConflictBasis = 'within_source' | 'across_sources';
export type SolAnswerState =
  | 'supported'
  | 'inference'
  | 'conflicting'
  | 'unknown'
  /**
   * **GENERAL HELP — the answer asserts no Crewlink fact.** Arithmetic, an
   * explanation, a drafted message, or Sol saying what Crewlink can and cannot
   * do. It carries no source cards because it cites nothing, which is how the
   * distinction reaches Brandon without teaching him a label.
   */
  | 'general';

export interface SolSourceIdentity {
  readonly domain: string;
  readonly id: string;
}

/**
 * One validated claim. **A DISCRIMINATED UNION, mirroring the server's** — a
 * supported claim has no `conflictBasis` field at all, so a renderer cannot
 * read one off a claim that never had one.
 */
export type SolValidatedClaim =
  | {
      readonly id: string;
      readonly text: string;
      readonly grounding: 'supported';
      readonly sources: readonly SolSourceIdentity[];
    }
  | {
      readonly id: string;
      readonly text: string;
      readonly grounding: 'inference';
      readonly sources: readonly SolSourceIdentity[];
    }
  | {
      readonly id: string;
      readonly text: string;
      readonly grounding: 'conflicting';
      readonly conflictBasis: SolConflictBasis;
      readonly sources: readonly SolSourceIdentity[];
    }
  | {
      readonly id: string;
      readonly text: string;
      readonly grounding: 'general';
      /** Always empty. A general claim cites nothing, by contract. */
      readonly sources: readonly SolSourceIdentity[];
    };

/**
 * A cited Capture, built on the server from evidence that was actually sent.
 *
 * **THE CLIENT NEVER INVENTS ONE AND NEVER REPAIRS ONE.** If a claim cites a
 * card that is not here, that is a contract failure, not a rendering puzzle.
 */
export interface SolCaptureSourceCard {
  readonly source: { readonly domain: 'capture'; readonly id: string };
  readonly project: SolProjectRef;
  readonly captureNumber: number;
  readonly status: string;
  readonly createdAt: string;
  readonly caption: string | null;
  readonly locationDisplay: string | null;
}

/** Crewlink's own Capture counts for one Project. **The database's arithmetic.** */
export interface SolProjectCaptureCounts {
  readonly open: number;
  readonly repaired: number;
  readonly closed: number;
}

/**
 * A cited PROJECT (M11 — the Project grounding fix).
 *
 * **A SEPARATE TYPE, NOT A CAPTURE CARD WITH HOLES IN IT.** A Project has no
 * CAP number, no caption and no location; giving the Capture card four nullable
 * fields so one shape could serve both would make "a Capture card with no
 * Capture number" representable, which is the mistake this codebase refuses
 * everywhere else. `source.domain` is the discriminant and it already existed.
 */
export interface SolProjectSourceCard {
  readonly source: { readonly domain: 'project'; readonly id: string };
  readonly project: SolProjectRef;
  /** The PROJECT's own status — never a Capture status. */
  readonly status: string;
  readonly createdAt: string;
  /** True for the Project on screen. */
  readonly isCurrent: boolean;
  /** Present only when the lookup computed them; null otherwise. */
  readonly counts: SolProjectCaptureCounts | null;
}

/**
 * A cited SAVED REPORT (M11 / I-069). **Product-facing fields only** — the
 * frozen description he wrote, the counts, the filename — and never the
 * snapshot, the PDF or a storage path: those have no field here to land in.
 */
export interface SolReportSourceCard {
  readonly source: { readonly domain: 'report'; readonly id: string };
  readonly project: SolProjectRef;
  readonly filename: string;
  readonly reportDate: string;
  readonly createdAt: string;
  readonly pageCount: number;
  readonly description: string | null;
  readonly captureCount: number | null;
  readonly photoCount: number | null;
}

/**
 * A cited PHOTO (M11 / I-071). **A photo card, never a Capture card pretending
 * to be one**: which Capture, before or after, its place in the order, when it
 * was added, the Capture's caption. The client may show a thumbnail through its
 * existing signed-URL port from `clientPhotoUuid` + `sourceMime`; the server
 * never sent the model any of that.
 */
export interface SolPhotoSourceCard {
  readonly source: { readonly domain: 'photo'; readonly id: string };
  readonly project: SolProjectRef;
  readonly captureId: string;
  readonly captureNumber: number;
  readonly photoClass: 'before' | 'after';
  readonly position: number;
  readonly createdAt: string;
  readonly captureCaption: string | null;
  readonly clientPhotoUuid: string;
  readonly sourceMime: string;
}

/** The cited COMPANY DETAILS (M11 / I-072). The fact of a logo, never the logo. */
export interface SolCompanySourceCard {
  readonly source: { readonly domain: 'company'; readonly id: string };
  readonly companyName: string;
  readonly contactEmail: string | null;
  readonly contactPhone: string | null;
  readonly hasLogo: boolean;
  readonly updatedAt: string;
}

/**
 * **FIVE KINDS, ONE DISCRIMINANT** (I-074). Every renderer switches on
 * `source.domain` exhaustively; a card of an unknown kind is a compile error,
 * not a mislabelled box.
 */
/**
 * A cited FOLLOW-UP (M12 / S12-03).
 *
 * **THE ID IS A SERVER-ISSUED HANDLE (`f1`, `f2`, …), NOT A ROW ID.** The
 * screen never needs the row's identity to show what the answer was built
 * from, and Sol cannot act on a follow-up, so there is nothing here to act
 * with.
 *
 * **`dueTime` AND `dueAt` ARE NULL ON A DAY-ONLY FOLLOW-UP.** The shape is the
 * guarantee: there is no field a renderer could invent an o'clock from
 * (`PRODUCT_TRUTH` §13.1).
 */
export interface SolMyDaySourceCard {
  readonly source: { readonly domain: 'my_day'; readonly id: string };
  readonly title: string;
  readonly description: string | null;
  readonly bucket: 'overdue' | 'today' | 'upcoming';
  readonly scheduleKind: 'day_only' | 'timed';
  readonly dueDate: string;
  readonly dueTime: string | null;
  readonly dueAt: string | null;
  readonly projectName: string | null;
  readonly captureNumber: number | null;
  readonly timeZone: string;
}

/**
 * A cited EARLIER CONVERSATION (M11 / D-041). **Provenance for a recall
 * answer**: the title, the origin, when it was last active and the bounded
 * excerpt Sol was shown — so he can open the conversation and read the whole
 * thing. It is a record of what was SAID; nothing on it is current truth.
 */
export interface SolConversationSourceCard {
  readonly source: { readonly domain: 'conversation'; readonly id: string };
  readonly title: string | null;
  readonly startedContext: 'project' | 'global';
  readonly projectId: string | null;
  readonly projectName: string | null;
  readonly lastActivityAt: string;
  readonly excerpt: string;
}

/**
 * One cited PUBLIC WEB PAGE (M11 Checkpoint B / I-078).
 *
 * **THE URL CAME FROM CREWLINK'S OWN RECORD OF THE SEARCH**, not from anything
 * the model wrote — the model cited a handle (`w1`), and the server decided
 * what that handle points at. The browser still validates it again before
 * making it clickable (`isSafeWebUrl`), because an untrusted URL crossing one
 * more boundary is worth one more check.
 */
export interface SolWebPageSourceCard {
  readonly source: { readonly domain: 'web'; readonly id: string };
  readonly kind: 'page';
  readonly title: string;
  readonly url: string;
  readonly host: string;
  readonly excerpt: string | null;
  readonly publishedAt: string | null;
  readonly retrievedAt: string;
}

/**
 * One cited LIVE DATA FEED (2026-08-25) — a named real-time source that has
 * **no webpage behind it**. The provider returns these for whole classes of
 * question: weather, sport, market data.
 *
 * **THERE IS NO `url` FIELD, AND THAT IS THE WHOLE DESIGN.** The card cannot be
 * given a link, cannot be made to look tappable, and cannot be handed an
 * invented URL to satisfy a layout — because the type has nowhere to put one.
 */
export interface SolWebFeedSourceCard {
  readonly source: { readonly domain: 'web'; readonly id: string };
  readonly kind: 'feed';
  readonly name: string;
  readonly retrievedAt: string;
}

export type SolWebSourceCard = SolWebPageSourceCard | SolWebFeedSourceCard;

/**
 * One cited LONG-TERM MEMORY — something he asked Sol to remember.
 *
 * **IT IS NOT A CREWLINK RECORD, AND THE CARD MUST NEVER LOOK LIKE ONE.** It is
 * a remembered snapshot of what he said once; a current record outranks it
 * whenever they disagree. `savedAt` is when Crewlink last wrote the memory, so
 * he can see how old the statement is — a two-year-old "ABC handles temporary
 * power" is a different kind of answer from last week's.
 */
export interface SolMemorySourceCard {
  readonly source: { readonly domain: 'memory'; readonly id: string };
  readonly memoryKind: 'preference' | 'instruction' | 'business_context';
  readonly content: string;
  readonly savedAt: string;
}

export type SolSourceCard =
  | SolCaptureSourceCard
  | SolProjectSourceCard
  | SolReportSourceCard
  | SolPhotoSourceCard
  | SolCompanySourceCard
  | SolConversationSourceCard
  | SolMyDaySourceCard
  | SolWebSourceCard
  | SolMemorySourceCard;

/**
 * **THE ONE PLACE THE BROWSER DECIDES WHICH KIND OF CARD IT HAS.** The
 * discriminant is nested, and TypeScript does not narrow a union through a
 * nested property — so without these the renderer would need a cast, which is
 * the same as not checking at all.
 */
export function isCaptureSourceCard(card: SolSourceCard): card is SolCaptureSourceCard {
  return card.source.domain === 'capture';
}

export function isMemorySourceCard(card: SolSourceCard): card is SolMemorySourceCard {
  return card.source.domain === 'memory';
}

export function isProjectSourceCard(card: SolSourceCard): card is SolProjectSourceCard {
  return card.source.domain === 'project';
}

export function isReportSourceCard(card: SolSourceCard): card is SolReportSourceCard {
  return card.source.domain === 'report';
}

export function isPhotoSourceCard(card: SolSourceCard): card is SolPhotoSourceCard {
  return card.source.domain === 'photo';
}

export function isCompanySourceCard(card: SolSourceCard): card is SolCompanySourceCard {
  return card.source.domain === 'company';
}

export function isConversationSourceCard(card: SolSourceCard): card is SolConversationSourceCard {
  return card.source.domain === 'conversation';
}

export function isMyDaySourceCard(card: SolSourceCard): card is SolMyDaySourceCard {
  return card.source.domain === 'my_day';
}

export function isWebSourceCard(card: SolSourceCard): card is SolWebSourceCard {
  return card.source.domain === 'web';
}

export function isWebPageSourceCard(card: SolSourceCard): card is SolWebPageSourceCard {
  return isWebSourceCard(card) && card.kind === 'page';
}

export function isWebFeedSourceCard(card: SolSourceCard): card is SolWebFeedSourceCard {
  return isWebSourceCard(card) && card.kind === 'feed';
}

/**
 * **THE BROWSER'S OWN URL CHECK, DUPLICATED ON PURPOSE** (I-078).
 *
 * The server already refused anything that is not `http:` or `https:`. This is
 * the check that stands between a stored URL and an `href` the person can tap,
 * and it lives here because it must run in the render path — a card whose URL
 * does not pass is shown as plain text rather than as a link.
 *
 * `javascript:`, `data:`, `file:` and every custom scheme a native shell might
 * hand to another application are refused, and a URL that does not parse is
 * refused rather than guessed at.
 */
export function isSafeWebUrl(raw: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return false;
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false;
  return parsed.hostname !== '';
}

// ===========================================================================
// Failures
// ===========================================================================

export type SolReasoningFailureKind =
  | 'reasoning_disabled'
  | 'reasoning_unconfigured'
  | 'provider_unavailable'
  | 'provider_incomplete'
  /**
   * **CREWLINK REFUSED TO SPEND, AND SAYS SO (H-4).** Sol is working on this
   * person's previous request. Distinct from an outage because the honest
   * answer — and the useful next action — are different.
   */
  | 'sol_busy'
  /** The internal hourly provider-spend brake refused it. Also not an outage. */
  | 'sol_rate_limited'
  | 'invalid_model_output'
  | 'citation_validation_failed'
  /**
   * **MY DAY CANNOT BE WORKED OUT WITHOUT A SAVED TIMEZONE** (M12 / S12-03).
   * Its own kind because the alternative was reporting an empty day: *"you have
   * nothing today"* and *"Crewlink cannot tell what today is for you"* are
   * opposite statements, and the reassuring one is the one a person acts on.
   */
  | 'my_day_timezone_unset'
  | 'internal_error';

/**
 * The validator's reason codes.
 *
 * **INTERNAL VOCABULARY. NEVER RENDERED.** They are typed here only so the
 * mapping in `sol-wording.ts` can be exhaustive; every one of them resolves to
 * Crewlink's own words before a screen sees it.
 */
export type SolRejectionReason =
  | 'malformed_root'
  | 'unexpected_field'
  | 'invalid_kind'
  | 'claim_count_invalid'
  | 'claim_text_invalid'
  | 'claim_text_too_long'
  | 'answer_text_too_long'
  | 'invalid_grounding'
  | 'conflict_basis_invalid'
  | 'source_shape_invalid'
  | 'source_domain_unsupported'
  | 'source_not_sent'
  | 'source_count_invalid'
  | 'sources_not_distinct'
  | 'uuid_in_prose';

// ===========================================================================
// Speech authorization (S09-04 / Pass B)
// ===========================================================================

/**
 * Permission to have ONE specific answer read aloud, issued by the server that
 * validated it.
 *
 * **THE BROWSER NEVER AUTHORS SPEAKABLE TEXT.** `chunks` are the server's own
 * canonical strings; Read Aloud sends them back verbatim and the server
 * re-hashes them against the ticket. A client that could compose its own text
 * would be a general-purpose text-to-speech proxy with Crewlink's bill
 * attached.
 *
 * `null` whenever speech is switched off, unconfigured, or the answer is not
 * eligible — which renders as no Read Aloud control at all.
 */
export interface SolSpeechAuthorization {
  /** Opaque to the client. Signed, user-bound, content-bound, short-lived. */
  readonly ticket: string;
  /** The canonical chunks, in speaking order. */
  readonly chunks: readonly string[];
}

// ===========================================================================
// Confirmed actions (M11 / S11-01)
// ===========================================================================

/**
 * A proposal to change ONE Capture's status, waiting for a human to confirm it.
 *
 * **THE TICKET IS THE ONLY THING THAT TRAVELS BACK.** Confirm sends `ticket`
 * and nothing else; every value the write acts on is read out of that signed
 * value on the server. **The `display` fields are for READING** — they say what
 * Brandon is agreeing to, and editing them in a browser changes what he sees
 * and nothing about what happens.
 *
 * `null` whenever Sol proposed nothing: no interpretation, not exactly one
 * Capture, a Capture that already holds that status, a bulk request, or a
 * server with confirmed actions switched off. **All of those render as no
 * card**, never as a card that apologises when tapped.
 */
export interface SolActionProposal {
  readonly type: 'capture_status_change';
  /** Opaque to the client. Signed, user-bound, target-bound, short-lived. */
  readonly ticket: string;
  /** Unix seconds. Used to stop offering a proposal that cannot succeed. */
  readonly expiresAt: number;
  readonly display: {
    readonly projectId: string;
    readonly projectName: string | null;
    readonly captureId: string;
    readonly captureNumber: number;
    readonly locationDisplay: string | null;
    readonly fromStatus: CaptureStatus;
    readonly toStatus: CaptureStatus;
  };
}

/**
 * A proposed MY DAY change (WD-2) — one of the five verbs, resolved and
 * canonicalized by the server, waiting for a human to confirm it. The same
 * contract as the Capture proposal: the ticket is the only thing that travels
 * back, and every display field is for reading, never for authority. **There
 * is deliberately no id of any kind in this shape.**
 */
export type SolMyDayProposalType =
  'my_day_create' | 'my_day_done' | 'my_day_dismiss' | 'my_day_edit' | 'my_day_reschedule';

export interface SolMyDayProposalChange {
  readonly field: 'title' | 'description' | 'project' | 'priority';
  readonly from: string | null;
  readonly to: string | null;
}

export interface SolMyDayProposalDisplay {
  readonly action: 'create' | 'done' | 'dismiss' | 'edit' | 'reschedule';
  /** The target's `f#` handle in this turn's evidence — null for create. */
  readonly handle: string | null;
  readonly title: string;
  readonly dayOnly: boolean;
  readonly dueDate: string;
  /** `HH:MM`, only when timed. */
  readonly dueTime: string | null;
  /** The canonical Crewlink zone a timed value is authored in. */
  readonly timeZone: string | null;
  readonly recurring: boolean;
  readonly recurRule: string | null;
  readonly projectName: string | null;
  readonly priority: 'normal' | 'high';
  /** Create only. */
  readonly description: string | null;
  /** Dismiss only: WHICH dismissal — stated explicitly before Confirm. */
  readonly mode: 'dismiss' | 'skip_occurrence' | 'end_series' | null;
  /** Edit only: the meaningful old → new lines. */
  readonly changes: readonly SolMyDayProposalChange[] | null;
  /** Reschedule only: where the current occurrence sits today. */
  readonly from: { readonly dueDate: string; readonly dueTime: string | null } | null;
}

export interface SolMyDayProposal {
  readonly type: SolMyDayProposalType;
  readonly ticket: string;
  readonly expiresAt: number;
  readonly display: SolMyDayProposalDisplay;
}

/** ANY proposal a turn can carry — the Capture status change, or a My Day verb. */
export type SolProposal = SolActionProposal | SolMyDayProposal;

/** The one place that tells the two card kinds apart. */
export function isMyDayProposal(proposal: SolProposal): proposal is SolMyDayProposal {
  return proposal.type !== 'capture_status_change';
}

// ===========================================================================
// The result union
// ===========================================================================

/**
 * THE READ-DOMAIN FAMILY — what kind of Crewlink record a read was ABOUT.
 *
 * Every result that names a domain gets it from the capability the server
 * executed — never from a Capture default and never from the person's wording.
 * The defect this closes: zero saved reports once arrived as
 * `no_evidence / domain: 'capture'` and the screen said "Crewlink found no
 * Captures matching that" about reports.
 */
export type SolReadDomain =
  | 'capture'
  | 'project'
  | 'report'
  | 'photo'
  | 'company'
  | 'conversation'
  /** HIS FOLLOW-UPS (M12 / S12-03) — its own domain, so an empty day says so. */
  | 'my_day'
  /** PUBLIC WEB (I-078) — not a Crewlink read, and never Crewlink truth. */
  | 'web';

// ===========================================================================
// Long-term memory (M11 / D-040)
// ===========================================================================

export type SolMemoryKind = 'preference' | 'instruction' | 'business_context';
export type SolMemoryOrigin = 'explicit' | 'automatic';

/**
 * One memory as the server shows it: the id (for Forget / Undo), the kind, the
 * origin and the words. **Display fields. Never authority, never evidence.**
 */
export interface SolMemoryCard {
  readonly id: string;
  readonly kind: SolMemoryKind;
  readonly origin: SolMemoryOrigin;
  readonly content: string;
}

export type SolMemoryRefusalReason =
  | 'not_from_current_statement'
  | 'sensitive'
  | 'target_not_sent'
  | 'too_long'
  | 'empty'
  | 'business_context_not_automatic'
  | 'unknown_kind'
  | 'unavailable';

export interface SolGroundedAnswer {
  readonly kind: 'grounded_answer';
  readonly domain: SolReadDomain;
  readonly answerState: SolAnswerState;
  readonly claims: readonly SolValidatedClaim[];
  /** Crewlink's own sentence when the state is `unknown`; null otherwise. */
  readonly unknownText: string | null;
  readonly sourceCards: readonly SolSourceCard[];
  readonly totalMatchCount: number;
  /**
   * Crewlink's OWN exact count for the same set, when the turn was a compound
   * read (M11 / I-067) — absent otherwise.
   *
   * **THE NUMBER AND THE CARDS ARE TWO ANSWERS TO ONE REQUEST.** The cards are
   * a bounded page; this is the database's total for the identical filters. The
   * screen states it itself, so *"how many are there, and show them"* cannot
   * come back with the cards and no number because the model's sentence
   * happened not to mention one.
   */
  readonly countedTotal?: number | null;
  readonly retrievedCandidateCount: number;
  readonly evidenceSentCount: number;
  readonly evidenceCharacterCount: number;
  readonly evidenceBounded: boolean;
  /** How many PROJECTS this answer could cite. 0 on every Capture answer. */
  readonly projectsSentCount: number;
  /** True when Crewlink listed only the first page of his Projects. */
  readonly projectsBounded: boolean;
  readonly scope: SolScopeFacts;
  readonly projects: readonly SolProjectRef[];
  /** Additive, S09-04. Absent on a server that predates speech. */
  readonly speech?: SolSpeechAuthorization | null;
  /** Additive, M11. Absent on a server that predates confirmed actions. */
  readonly proposal?: SolProposal | null;
}

/**
 * Evidence as the close-match path returns it.
 *
 * **NOT a factual answer.** These are the records Crewlink is least sure about,
 * and the screen has to say so rather than list them as findings.
 */
export interface SolCandidateEvidence {
  readonly source: SolSourceRef;
  readonly project: SolProjectRef;
  readonly captureNumber: number;
  readonly status: CaptureStatus;
  readonly createdAt: string;
  readonly acceptedTranscript: string | null;
  readonly caption: string | null;
  readonly locationDisplay: string | null;
}

/**
 * Everything `sol-ask` can answer with.
 *
 * **`reasoning_required` IS DELIBERATELY ABSENT.** It is the internal boundary
 * between S09-03a and S09-03b and is resolved before the response is written,
 * so it cannot appear at the public endpoint. If one ever does, that is a
 * contract failure — `presentSolResult` treats it as one rather than inventing
 * a screen for it.
 */
export type SolAnswerResult =
  | {
      readonly kind: 'needs_clarification';
      readonly domain: SolReadDomain;
      readonly reason: ClarificationReason;
      readonly phrase: string | null;
      readonly candidateProjects: readonly SolProjectRef[];
      readonly scopesSearched: readonly CaptureScope[];
      readonly projectsRepresented: readonly string[];
      readonly sources: readonly SolSourceRef[];
    }
  | {
      readonly kind: 'unsupported';
      readonly domain: 'capture';
      readonly reason: UnsupportedReason;
      readonly phrase: string;
    }
  | {
      readonly kind: 'no_evidence';
      /**
       * **THE EXECUTED READ'S DOMAIN.** A supported capability that found
       * nothing — zero reports, zero photos, no company details — is worded as
       * ITSELF by `describeNoEvidence`, never as an empty Capture search and
       * never as an unsupported request.
       */
      readonly domain: SolReadDomain;
      readonly totalMatchCount: 0;
      readonly scope: SolScopeFacts;
    }
  | {
      readonly kind: 'close_matches';
      readonly domain: 'capture';
      readonly totalMatchCount: number;
      readonly candidates: readonly SolCandidateEvidence[];
      readonly sources: readonly SolSourceRef[];
      readonly projects: readonly SolProjectRef[];
      readonly scope: SolScopeFacts;
    }
  | {
      readonly kind: 'count';
      /** Projects can be counted too (M11 semantic plan). */
      /** Photos can be counted too (I-071). */
      readonly domain: 'capture' | 'project' | 'photo';
      readonly totalMatchCount: number;
      readonly scope: SolScopeFacts;
    }
  | {
      readonly kind: 'oversized_evidence';
      readonly domain: 'capture';
      readonly source: SolSourceRef;
      readonly project: SolProjectRef;
      readonly captureNumber: number;
      readonly characterCount: number;
      readonly ceiling: number;
      readonly totalMatchCount: number;
      readonly scope: SolScopeFacts;
    }
  | SolGroundedAnswer
  | {
      readonly kind: 'reasoning_failed';
      readonly domain: SolReadDomain;
      readonly failure: SolReasoningFailureKind;
      readonly reason: SolRejectionReason | null;
      readonly scope: SolScopeFacts;
      readonly totalMatchCount: number;
    }
  // ---- Memory outcomes (M11 / D-040). Deterministic; Crewlink's own words. ----
  | {
      readonly kind: 'memory_saved';
      readonly memory: SolMemoryCard;
      /** The words of the memory this one replaced, or null for a plain remember. */
      readonly replacedContent: string | null;
    }
  | { readonly kind: 'memory_forgotten'; readonly memory: SolMemoryCard }
  | {
      readonly kind: 'memory_clarification';
      /** Several matches to choose between, or EMPTY when nothing matched. */
      readonly candidates: readonly SolMemoryCard[];
      readonly description: string | null;
    }
  | { readonly kind: 'memory_clear_requested'; readonly activeCount: number }
  | { readonly kind: 'memory_refused'; readonly reason: SolMemoryRefusalReason };

/** The `domain:id` key. **The ONLY legal way to join a claim to a card.** */
export function solSourceKey(source: SolSourceIdentity): string {
  return `${source.domain}:${source.id}`;
}
